import React, { useContext, useMemo, useState } from 'react';

import { Grid, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import RosterToolbar from './RosterToolbar';
import StudentTableRow from './StudentTableRow';
import RosterDeleteModal from '../../../../../components/roster/RosterDeleteModal';
import RosterEditModal from '../../../../../components/roster/RosterEditModal';
import { PaginationContainer, TablePagination, useTablePagination } from '../../../../../components/TablePagination';
import { RosterContext } from '../../../RosterContext';

const StyledTable = styled(Table)`
    margin-bottom: 2rem;
`;

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

export const ClassDetailRoster = () => {
    const { t } = useTranslation('roster');
    const { roster, rosterData, openEditModal, openDeleteModal, selectedStudentId } = useContext(RosterContext);

    const [searchText, setSearchText] = useState('');

    const filteredRoster = useMemo(
        () =>
            roster?.filter((student) => {
                const includedInUserName = student.user_name
                    .toLocaleLowerCase()
                    .includes(searchText.toLocaleLowerCase());
                const includedInEmail = student.email?.toLocaleLowerCase().includes(searchText.toLocaleLowerCase());

                return includedInUserName || includedInEmail;
            }),
        [searchText, roster]
    );

    const { onRowsPerPageChange, paginatedValues, onPageChange, rowsPerPage, setPage, count, page } =
        useTablePagination(filteredRoster);

    const handleChangeSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSearchText(event.target.value);
        setPage(1);
    };

    const selectedUser = useMemo(
        () => roster.find((u) => u.student_id === selectedStudentId),
        [selectedStudentId, roster]
    );

    if (rosterData.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    return (
        <>
            <RosterToolbar searchText={searchText} handleChangeSearch={handleChangeSearch} />
            <StyledTable>
                <TableHead>
                    <TableRow>
                        <TableCell>{t('name')}</TableCell>
                        <TableCell>{t('Email')}</TableCell>
                        <TableCell>{t('role')}</TableCell>
                        <TableCell>{t('actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedValues.map((student) => (
                        <StudentTableRow key={student.student_id} student={student} />
                    ))}
                </TableBody>
            </StyledTable>
            <PaginationContainer>
                <TablePagination
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                />
            </PaginationContainer>

            <RosterEditModal open={openEditModal} data={selectedUser} />

            <RosterDeleteModal open={openDeleteModal} data={selectedUser} />
        </>
    );
};
